import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { UserProfile, SubscriptionTier, AttireSuggestion } from '../types/synclothe';
import { useToasts } from './TactileToasts';
import { useStyling } from '../context/StylingContext';
import { useConfirm } from '../context/ConfirmContext';
import MultiProviderLogin from './MultiProviderLogin';

interface StylingEngineProps {
  userId?: string;
  subscriptionTier: SubscriptionTier;
  onCreditUpdate: (credits: number) => void;
}

const EVENT_OPTIONS = [
    "Executive Boardroom", "Strategic Networking", "High-Impact Interview", "Wedding / Ceremonial Ritual", 
    "Traditional Celebration", "Heritage Festival", "Memorial / Solemn Occasion", "Art Gallery / Cultural Opening",
    "Creative Studio / Agency Session", "On-Site Leadership / Field Inspection", "Virtual Presence / Home Studio", 
    "Gala / Black Tie Awards", "Curated Date Night", "Nightlife / Exclusive Clubbing", "Music Festival", 
    "Transit / Airport Professional", "Resort / Leisure Escape", "Coastal / Yacht Deck", "Al Fresco / Garden Social",
    "Mindfulness / Studio Session", "Athletic Performance / Training", "Tactical / Alpine Trekking", 
    "Commencement / Graduation"
  ];
  
  const CULTURE_OPTIONS = [
    "South Asian",
    "East Asian",
    "West Asian/Middle Eastern",
    "African (West & East)",
    "Indigenous/Central American",
    "Nordic/Scandinavian",
    "Mediterranean",
    "Contemporary Global/Western"
  ];
  
  const STYLE_OPTIONS = [
    "Minimalist",
    "Classic Sartorial",
    "Avant-Garde",
    "Relaxed / Fluid",
    "Traditional / Heritage",
    "Athleisure Luxe",
    "Techwear / Utility",
    "Streetwear/Urban",
    "Resort/Bohemian"
  ];
  
  const VENUE_OPTIONS: UserProfile['venue'][] = ["Indoor (AC)", "Outdoor", "Semi-Open"];
const StylingEngine: React.FC<StylingEngineProps> = ({ userId, subscriptionTier, onCreditUpdate }) => {
  const { showToast } = useToasts();
  const { confirm } = useConfirm();
  const { 
    currentProfile: profile, 
    setCurrentProfile: setProfile, 
    currentSuggestions: suggestions, 
    setCurrentSuggestions: setSuggestions,
    wardrobeComment,
    searchHistory,
    activeHistoryId,
    addToHistory,
    updateHistoryItemSuggestions,
    loadHistoryItem,
    shownWardrobeIds,
    resetRotation,
    isProcessing,
    executeStyling
  } = useStyling();

  const isExplorer = subscriptionTier === 'Explorer';
  const isPremium = subscriptionTier === 'Premium';
  const [showHistory, setShowHistory] = useState(false);
  const [isCustomEvent, setIsCustomEvent] = useState(true); // Default to custom event field
  const [dateError, setDateError] = useState(false);
  const [eventError, setEventError] = useState(false);
  const [locationError, setLocationError] = useState(false);
  const [renderingCardIdx, setRenderingCardIdx] = useState<number | null>(null);
  const [openingCardIdx, setOpeningCardIdx] = useState<number | null>(null);
  const [isOnline, setIsOnline] = useState<boolean>(typeof navigator !== 'undefined' ? navigator.onLine : true);

  // Network Connectivity Monitoring
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      showToast("Network connection restored. Online.", "sync");
    };
    const handleOffline = () => {
      setIsOnline(false);
      showToast("Network connection lost. Offline mode active.", "error");
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [showToast]);

  // Explorer Demographics Modal State
  const [showDemographicsModal, setShowDemographicsModal] = useState(false);
  const [selectedGender, setSelectedGender] = useState<'Male' | 'Female' | 'Other' | 'Non-binary'>(
    profile.gender || 'Female'
  );
  const [selectedAge, setSelectedAge] = useState<number>(
    profile.age && profile.age >= 16 ? profile.age : 25
  );

  // Synchronize modal state whenever profile changes
  useEffect(() => {
    if (profile.gender) setSelectedGender(profile.gender);
    if (profile.age && profile.age >= 16) setSelectedAge(profile.age);
  }, [profile.gender, profile.age]);

  // Close demographics modal on Escape key
  useEffect(() => {
    if (!showDemographicsModal) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowDemographicsModal(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showDemographicsModal]);

  // Get current local time for validation
  const getCurrentDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Idle Return Auto-Advance: If user returns after the selected time has passed, auto-advance to current time
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const currentNow = getCurrentDateTime();
        if (profile.dateTime && profile.dateTime < currentNow) {
          setProfile({ ...profile, dateTime: currentNow });
          setDateError(false);
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [profile, setProfile]);

  if (!userId) {
    return (
      <div className="p-4 md:p-8 min-h-screen bg-dk-900 flex flex-col items-center justify-center text-center">
        <div className="max-w-md space-y-6">
          <div className="w-20 h-20 bg-azure/10 rounded-full flex items-center justify-center mx-auto border border-azure/20 animate-pulse">
            <svg className="w-10 h-10 text-azure-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path></svg>
          </div>
          <div>
            <h2 className="text-2xl font-black text-white italic uppercase tracking-tighter">Welcome to Synclothe</h2>
            <p className="text-xs font-bold text-azure-400 uppercase tracking-widest mt-1">AI Haute Couture Intelligence & Digital Wardrobe</p>
          </div>

          <MultiProviderLogin className="mt-4" />
        </div>
      </div>
    );
  }

  const handleInputChange = (e: React.SyntheticEvent<HTMLInputElement | HTMLSelectElement>) => {
    const target = e.target as HTMLInputElement;
    const { name, type } = target;
    let value = target.value;

    if (type === 'checkbox') {
        setProfile({ ...profile, [name]: target.checked });
        return;
    }

    if (name === 'age') {
        setProfile({ ...profile, [name]: parseInt(value) || 0 });
        return;
    }

    if (name === 'dateTime') {
        setDateError(false);
    }

    if (name === 'event') {
        setEventError(false);
    }

    if (name === 'location') {
        setLocationError(false);
    }
    
    setProfile({ ...profile, [name]: value });
  };

  const handleExplorerDownloadClick = async () => {
    const proceed = await confirm(
      "Standard Feature Required",
      "Exporting and saving on-model style looks is reserved for Standard and Premium tiers. Would you like to view our subscription plans?"
    );
    if (proceed) {
      window.location.href = "/subscription";
    }
  };

  const handleDownloadImage = async (imageUrl: string, fileName: string) => {
    if (isExplorer) {
      handleExplorerDownloadClick();
      return;
    }
    try {
      if (!imageUrl) return;
      showToast("✨ Preparing style photo...", "logic");
      
      // 1. Convert Base64 or Remote URL into a true JPEG Blob
      let blob: Blob;
      if (imageUrl.startsWith('data:')) {
        const parts = imageUrl.split(',');
        const mime = parts[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
        const byteString = atob(parts[1]);
        const ia = new Uint8Array(byteString.length);
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }
        blob = new Blob([ia], { type: mime });
      } else {
        const res = await fetch(imageUrl, { mode: 'cors' });
        if (!res.ok) throw new Error("Image fetch failed");
        blob = await res.blob();
      }

      const cleanFileName = `${fileName.replace(/[^a-zA-Z0-9_-]/g, '_')}.jpg`;
      const imageFile = new File([blob], cleanFileName, { type: 'image/jpeg' });

      // 2. Mobile Priority: Native Web Share Sheet -> "Save Image to Photos" (Mobile only)
      const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || 
                             (navigator.maxTouchPoints > 2 && /Macintosh/i.test(navigator.userAgent));

      if (isMobileDevice && navigator.canShare && navigator.canShare({ files: [imageFile] })) {
        try {
          await navigator.share({
            files: [imageFile],
            title: 'Synclothe Curated Look',
            text: 'Saved from Synclothe AI Styling Engine'
          });
          showToast("✨ Photo saved / shared!", "success");
          return;
        } catch (shareErr: any) {
          // If user dismissed the share modal, don't trigger secondary download
          if (shareErr.name === 'AbortError') return;
        }
      }

      // 3. Desktop / Laptop: Direct Anchor Download to Downloads folder
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = blobUrl;
      a.download = cleanFileName;
      a.setAttribute('download', cleanFileName);
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
      }, 1000);
      showToast("✨ Photo saved to your device!", "success");
    } catch (err) {
      // Direct browser fallback
      try {
        const a = document.createElement('a');
        a.href = imageUrl;
        a.download = `${fileName.replace(/[^a-zA-Z0-9_-]/g, '_')}.jpg`;
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        showToast("✨ Photo opened.", "success");
      } catch (fallbackErr) {
        showToast("⚠️ Could not download photo.", "error");
      }
    }
  };

  const handleRenderSingleLook = async (item: AttireSuggestion, idx: number) => {
    if (renderingCardIdx !== null || openingCardIdx !== null) return;
    setRenderingCardIdx(idx);
    showToast("✨ Synthesizing on-model look...", "logic");

    try {
      const response = await fetch('/api/renderStyleImage', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId || ''
        },
        body: JSON.stringify({
          item: {
            id: item.id,
            name: item.name,
            description: item.description,
            visualPrompt: item.visualPrompt,
            hairstyle: item.hairstyle,
            accessories: item.accessories?.map(a => typeof a === 'string' ? a.slice(0, 300) : a),
            wardrobeIds: item.wardrobeIds
          },
          context: {
            event: profile.event,
            location: profile.location,
            venue: profile.venue,
            gender: profile.gender,
            age: profile.age,
            culture: profile.culture,
            style: profile.style
          }
        })
      });

      let data: any = null;
      try {
        data = await response.json();
      } catch (jsonErr) {
        data = { error: response.statusText || "Communication Error" };
      }

      if (!response.ok || !data?.success || !data?.base64Image) {
        throw new Error(data?.error || "Failed to render on-model look.");
      }

      // Trigger 3D Wardrobe Vault door opening transition
      setOpeningCardIdx(idx);

      // Update suggestion in-place with high-fidelity visual
      const updatedSuggestions = suggestions.map((s, i) => {
        if (i === idx) {
          return {
            ...s,
            base64Image: data.base64Image,
            previewUrl: data.base64Image,
            isAiGenerated: true
          };
        }
        return s;
      });

      setSuggestions(updatedSuggestions);

      // Persist in-place to the active history record if revisiting or in session
      if (activeHistoryId) {
        updateHistoryItemSuggestions(activeHistoryId, updatedSuggestions);
      } else {
        addToHistory(profile, updatedSuggestions, wardrobeComment);
      }

      showToast("✨ On-model look synthesized successfully!", "success");

      // Conclude 3D door opening transition after animation finishes
      setTimeout(() => {
        setOpeningCardIdx(null);
      }, 1100);
    } catch (err: any) {
      showToast(err.message || "Failed to render on-model look. Please try again.", "error");
    } finally {
      setRenderingCardIdx(null);
    }
  };

  const validateInputs = (): boolean => {
    // Strategic Logic Validations
    const ev = (profile.event || '').trim();
    const evRepeatedChar = /^(.)\1+$/i.test(ev);
    const evHasLetters = /[a-zA-Z]/.test(ev);
    const evKeyboardMash = /^(asdf|qwerty|zxcv|hjkl|1234|test|foo|bar|null|undefined)$/i.test(ev);
    const evInvalidChars = /[<>{}[\]\\";]/.test(ev);

    if (!ev || ev.length < 2 || ev.length > 50 || !evHasLetters || evRepeatedChar || evKeyboardMash || evInvalidChars) {
        setEventError(true);
        showToast("Please enter a valid occasion or event.", "error");
        return false;
    }
    setEventError(false);

    const loc = (profile.location || '').trim();
    const isRepeatedChar = /^(.)\1+$/i.test(loc);
    const hasLetters = /[a-zA-Z]/.test(loc);
    const isKeyboardMash = /^(asdf|qwerty|zxcv|hjkl|1234|test|foo|bar)$/i.test(loc);

    if (!loc || loc.length < 3 || !hasLetters || isRepeatedChar || isKeyboardMash) {
        setLocationError(true);
        showToast("Please enter a valid city or location.", "error");
        return false;
    }
    setLocationError(false);

    if (profile.dateTime < getCurrentDateTime()) {
        setDateError(true);
        showToast("Please select future date", "error");
        return false;
    }
    setDateError(false);
    return true;
  };

  const handleInitiateStyling = () => {
    if (isProcessing) return;
    if (!validateInputs()) return;

    // Explorer Tier Demographics Requirement: Prompt for gender & age before synthesis
    if (isExplorer) {
      if (profile.gender) setSelectedGender(profile.gender);
      if (profile.age && profile.age >= 16) setSelectedAge(profile.age);
      setShowDemographicsModal(true);
    } else {
      executeStyling(profile);
    }
  };

  const handleConfirmDemographics = () => {
    if (isProcessing) return;
    const ageNum = Number(selectedAge);
    if (isNaN(ageNum) || ageNum < 16 || ageNum > 100) {
      showToast("Please enter a valid age between 16 and 100.", "error");
      return;
    }

    const updatedProfile: UserProfile = {
      ...profile,
      gender: selectedGender,
      age: ageNum
    };
    setProfile(updatedProfile);
    setShowDemographicsModal(false);
    executeStyling(updatedProfile);
  };

  const handleLoadHistory = (item: any) => {
    loadHistoryItem(item);
    setIsCustomEvent(!EVENT_OPTIONS.includes(item.profile.event));
    setShowHistory(false);
    showToast("Historical logic state restored.", "logic");
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-dk-900 text-gray-200 p-4 md:p-8 flex flex-col items-center read-only-ui relative">
      
      {/* History Drawer Overlay */}
      {showHistory && (
        <div className="fixed inset-0 z-[100] flex justify-end animate-fade-in">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowHistory(false)}></div>
          <div className="relative w-full max-w-md bg-dk-900 border-l border-white/10 shadow-2xl p-8 overflow-y-auto animate-slide-in-right">
            <div className="flex justify-between items-center mb-12">
               <div>
                  <h2 className="text-2xl font-black text-white italic uppercase tracking-tighter">Device History</h2>
                  <p className="text-[10px] text-gray-300 font-bold uppercase tracking-widest mt-1">Last 10 Intelligence Cycles</p>
               </div>
               <button onClick={() => setShowHistory(false)} className="p-2 hover:bg-white/5 rounded-full transition-colors">
                  <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
               </button>
            </div>

            {searchHistory.length === 0 ? (
              <div className="h-64 flex flex-col items-center justify-center text-center opacity-30">
                <div className="w-12 h-12 border border-dashed border-white/20 rounded-full mb-4"></div>
                <p className="text-[10px] font-black uppercase tracking-widest">No device history found</p>
              </div>
            ) : (
              <div className="space-y-6">
                {searchHistory.map((item) => (
                  <div 
                    key={item.id} 
                    onClick={() => handleLoadHistory(item)}
                    className="glass-panel p-5 hover:border-azure/40 transition-all cursor-pointer group bg-white/[0.02]"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <span className="text-[8px] font-black text-azure-400 uppercase tracking-widest">{item.profile.event}</span>
                      <span className="text-[7px] text-gray-600 font-bold uppercase tracking-widest">
                        {new Date(item.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                       <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-azure-400">
                          <svg className="w-5 h-5 opacity-40" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM14 11a1 1 0 011 1v1h1a1 1 0 110 2h-1v1a1 1 0 11-2 0v-1h-1a1 1 0 110-2h1v-1a1 1 0 011-1z"></path>
                          </svg>
                       </div>
                       <div className="flex-1">
                          <p className="text-xs font-bold text-gray-300 group-hover:text-white transition-colors">{item.profile.location}</p>
                          <p className="text-[9px] text-gray-300 uppercase tracking-tighter">{item.profile.style} • {item.profile.culture}</p>
                       </div>
                    </div>
                    {item.suggestions && item.suggestions.length > 0 && (
                      <div className="flex items-center gap-1.5 mt-3 pt-2.5 border-t border-white/5">
                        <div className="flex items-center gap-1">
                          {item.suggestions.map((s, sIdx) => (
                            <span 
                              key={sIdx} 
                              title={s.isAiGenerated ? `Look ${sIdx + 1}: Unveiled` : `Look ${sIdx + 1}: Unopened Door`}
                              className={`w-2 h-2 rounded-full transition-all ${s.isAiGenerated ? 'bg-azure-400 shadow-[0_0_5px_#007fff]' : 'bg-white/20 border border-white/30'}`}
                            />
                          ))}
                        </div>
                        <span className="text-[7.5px] text-gray-400 font-mono uppercase tracking-wider ml-auto">
                          {item.suggestions.filter(s => s.isAiGenerated).length}/{item.suggestions.length} Unveiled
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="text-center mb-8 md:mb-12 mt-4 md:mt-8 animate-fade-in">
        <h1 className="text-2xl md:text-4xl font-black text-white italic tracking-tighter leading-none mb-4">
          Logic meets <span className="text-azure-500">Digital Fashion</span>
        </h1>
        <p className="text-[10px] md:text-base font-inter uppercase tracking-[0.2em] md:tracking-[0.4em] text-gray-300 font-bold max-w-xs md:max-w-none mx-auto">
          Strategic attire orchestration using weighted climate and cultural logic.
        </p>
      </div>

      <div className="flex flex-col gap-8 md:gap-12 w-full max-w-4xl animate-fade-in">
        <div className="glass-panel rounded-2xl p-6 md:p-8 shadow-2xl">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-white/10 pb-4 mb-8">
            <h2 className="text-lg font-bold text-white tracking-widest uppercase italic">Contextual Profiling</h2>
            <div className="flex items-center gap-2 sm:gap-4 w-full sm:w-auto justify-between sm:justify-end">
               {!isOnline && (
                  <span className="text-[9px] sm:text-[10px] font-black px-2.5 py-1 bg-red-500/10 text-red-400 rounded-full border border-red-500/30 uppercase tracking-widest flex items-center gap-1.5 animate-pulse">
                     <span className="w-1.5 h-1.5 rounded-full bg-red-400"></span>
                     Offline
                  </span>
               )}
               <button 
                  onClick={() => setShowHistory(true)}
                  className="text-[9px] font-black px-3 py-1 bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white rounded-full border border-white/10 uppercase tracking-widest transition-all"
               >
                  Device History
               </button>
               <span className="text-[9px] sm:text-[10px] font-black px-3 py-1 bg-azure/10 text-azure-400 rounded-full border border-azure/20 uppercase tracking-widest whitespace-nowrap">
                  Tier: {subscriptionTier}
               </span>
            </div>
          </div>
          
          <div className="space-y-12">
            <section>
              <h3 className="block text-[10px] uppercase text-azure-400 font-black mb-4 tracking-[0.2em]">Environmental Context</h3>
              <div className="space-y-4">
                  <div>
                      <div className="flex justify-between items-center mb-1">
                          <p className={`text-[9px] font-bold uppercase transition-colors ${eventError ? 'text-red-400 font-black' : 'text-gray-300'}`}>Event Type</p>
                          {isCustomEvent && (
                              <button onClick={() => { setEventError(false); setIsCustomEvent(false); setProfile({...profile, event: EVENT_OPTIONS[0]}); }} className="text-[7px] text-azure-400 hover:text-white uppercase tracking-widest transition-colors">Select Preset</button>
                          )}
                      </div>
                      {isCustomEvent ? (
                          <input 
                              type="text"
                              id="styling-event-input"
                              name="event" 
                              placeholder="DEFINE CUSTOM OCCASION..." 
                              maxLength={50}
                              value={profile.event} 
                              autoComplete="off"
                              autoCapitalize="words"
                              autoCorrect="off"
                              spellCheck={false}
                              onChange={(e) => {
                                setEventError(false);
                                handleInputChange(e);
                              }} 
                              className={`w-full input-glass text-xs cursor-text transition-all ${
                                eventError 
                                  ? 'border-red-500 ring-2 ring-red-500/40 text-red-300 bg-red-950/20' 
                                  : ''
                              }`} 
                              autoFocus
                          />
                      ) : (
                          <select 
                              name="event" 
                              value={
                                EVENT_OPTIONS.find(opt => opt.toLowerCase() === profile.event.trim().toLowerCase()) ||
                                (EVENT_OPTIONS.includes(profile.event) ? profile.event : "Custom Event / Other...")
                              } 
                              onChange={(e) => {
                                  setEventError(false);
                                  if (e.target.value === "Custom Event / Other...") {
                                      setIsCustomEvent(true);
                                      setProfile({...profile, event: ""});
                                  } else {
                                      handleInputChange(e);
                                  }
                              }} 
                              className={`w-full input-glass text-xs transition-all ${
                                eventError 
                                  ? 'border-red-500 ring-2 ring-red-500/40 text-red-300 bg-red-950/20' 
                                  : ''
                              }`}
                          >
                              {EVENT_OPTIONS.map(opt => <option key={opt} value={opt} className="bg-dk-800">{opt}</option>)}
                              <option value="Custom Event / Other..." className="bg-dk-800 italic text-azure-400">Custom Event / Other...</option>
                          </select>
                      )}
                      {eventError && (
                        <p className="text-[8px] text-red-400 font-bold uppercase tracking-wider mt-1 flex items-center gap-1">
                          <span>⚠️</span> Please enter a valid occasion or event
                        </p>
                      )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                      <div>
                          <p className={`text-[9px] mb-1 font-bold uppercase transition-colors ${dateError ? 'text-red-400 font-black' : 'text-gray-300'}`}>Date/Time</p>
                          <input 
                            name="dateTime" 
                            type="datetime-local" 
                            min={getCurrentDateTime()}
                            value={profile.dateTime} 
                            onChange={(e) => {
                              setDateError(false);
                              handleInputChange(e);
                            }} 
                            className={`w-full input-glass text-[10px] cursor-text transition-all ${
                              dateError 
                                ? 'border-red-500 ring-2 ring-red-500/40 text-red-300 bg-red-950/20' 
                                : ''
                            }`} 
                          />
                          {dateError && (
                            <p className="text-[8px] text-red-400 font-bold uppercase tracking-wider mt-1 flex items-center gap-1">
                              <span>⚠️</span> Please select future date
                            </p>
                          )}
                      </div>
                      <div>
                          <p className="text-[9px] text-gray-300 mb-1 font-bold uppercase">Venue</p>
                          <select name="venue" value={profile.venue} onChange={handleInputChange} className="w-full input-glass text-[10px]">
                              {VENUE_OPTIONS.map(opt => <option key={opt} value={opt} className="bg-dk-800">{opt}</option>)}
                          </select>
                      </div>
                  </div>
                  <div>
                    <input 
                      type="text"
                      id="styling-location-input"
                      name="location" 
                      placeholder="LOCATION (CITY)" 
                      value={profile.location} 
                      autoComplete="address-level2"
                      autoCapitalize="words"
                      autoCorrect="off"
                      spellCheck={false}
                      onChange={(e) => {
                        setLocationError(false);
                        handleInputChange(e);
                      }} 
                      className={`w-full input-glass text-xs cursor-text transition-all ${
                        locationError 
                          ? 'border-red-500 ring-2 ring-red-500/40 text-red-300 bg-red-950/20' 
                          : ''
                      }`} 
                    />
                    {locationError && (
                      <p className="text-[8px] text-red-400 font-bold uppercase tracking-wider mt-1 flex items-center gap-1">
                        <span>⚠️</span> Please enter a valid city or location
                      </p>
                    )}
                  </div>
              </div>
            </section>

            <section className={isExplorer ? 'opacity-40 grayscale pointer-events-none transition-all' : 'transition-all'}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="block text-[10px] uppercase text-azure-400 font-black tracking-[0.2em]">Culture & Style</h3>
                {isExplorer && <span className="text-[8px] font-black text-gold bg-gold/10 px-2 py-0.5 rounded border border-gold/20 uppercase">Unlock with Standard</span>}
              </div>
              <div className="space-y-4">
                  <select name="culture" value={profile.culture} onChange={handleInputChange} className="w-full input-glass text-xs" disabled={isExplorer}>
                      {CULTURE_OPTIONS.map(opt => <option key={opt} value={opt} className="bg-dk-800">{opt}</option>)}
                  </select>
                  <select name="style" value={profile.style} onChange={handleInputChange} className="w-full input-glass text-xs" disabled={isExplorer}>
                      {STYLE_OPTIONS.map(opt => <option key={opt} value={opt} className="bg-dk-800">{opt}</option>)}
                  </select>
                  <label className="flex items-center space-x-3 cursor-pointer group p-4 bg-white/5 rounded-lg border border-white/5">
                      <input type="checkbox" name="fabricSensitivity" checked={profile.fabricSensitivity} onChange={handleInputChange} disabled={isExplorer} className="w-4 h-4 rounded border-white/10 bg-white/5 text-azure focus:ring-azure" />
                      <span className="text-[10px] text-gray-400 group-hover:text-azure-400 font-bold uppercase tracking-widest transition-colors">Fabric Sensitivity Mode</span>
                  </label>
              </div>
            </section>

            <section className={!isPremium ? 'opacity-40 grayscale pointer-events-none transition-all' : 'transition-all'}>
               <div className="flex justify-between items-center mb-4">
                <h3 className="block text-[10px] uppercase text-azure-400 font-black tracking-[0.2em]">Wardrobe Synchronization</h3>
                {!isPremium && <span className="text-[8px] font-black text-gold bg-gold/10 px-2 py-0.5 rounded border border-gold/20 uppercase">Unlock with Premium</span>}
              </div>
              <div className="space-y-2">
                <label className="flex items-center space-x-3 cursor-pointer group p-4 bg-azure/5 rounded-lg border border-azure/10">
                    <input type="checkbox" name="wardrobeSync" checked={profile.wardrobeSync} onChange={handleInputChange} disabled={!isPremium} className="w-4 h-4 rounded border-white/10 bg-white/5 text-azure focus:ring-azure" />
                    <span className="text-[10px] text-gray-400 group-hover:text-azure-400 font-bold uppercase tracking-widest transition-colors">Sync Digital Closet Data</span>
                </label>
                {isPremium && profile.wardrobeSync && shownWardrobeIds.length > 0 && (
                  <div className="p-3 bg-dk-800 rounded border border-white/5 flex items-center justify-between text-[9px] uppercase tracking-wider text-gray-400">
                    <span>Rotation: {shownWardrobeIds.length} items styled in this session</span>
                    <button onClick={() => { resetRotation(); showToast("Closet rotation reset.", "success"); }} className="text-azure-400 font-black hover:text-azure transition-colors">
                      Reset Rotation
                    </button>
                  </div>
                )}
              </div>
            </section>

            <button onClick={handleInitiateStyling} disabled={isProcessing} className="w-full btn-azure uppercase tracking-[0.2em] text-xs font-black">
              {isProcessing ? 'Orchestrating Logic...' : 'Get Orchestrated Styles'}
            </button>
            {isExplorer && (
              <div className="mt-3 text-center">
                <button
                  type="button"
                  disabled={isProcessing}
                  onClick={() => !isProcessing && setShowDemographicsModal(true)}
                  className={`text-[10px] font-bold uppercase tracking-wider transition-colors inline-flex items-center gap-1.5 ${
                    isProcessing 
                      ? 'text-gray-600 cursor-not-allowed opacity-50 select-none pointer-events-none' 
                      : 'text-gray-400 hover:text-azure-400 cursor-pointer'
                  }`}
                  title={isProcessing ? 'Style generation in progress...' : 'Edit Demographics'}
                  aria-disabled={isProcessing}
                >
                  <span>Demographics:</span>
                  <span className={`font-mono px-2 py-0.5 rounded border transition-colors ${
                    isProcessing 
                      ? 'text-gray-500 bg-white/5 border-white/5' 
                      : 'text-azure-300 bg-azure-500/10 border-azure-500/20'
                  }`}>
                    {profile.gender || 'Female'}, {profile.age || 25} yrs
                  </span>
                  <span className={`text-[9px] ml-0.5 ${
                    isProcessing ? 'text-gray-600 no-underline' : 'text-azure-400 underline'
                  }`}>
                    Edit
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="flex-grow space-y-12">
          {suggestions.length > 0 ? (
            <div className="space-y-8 animate-fade-in">
              {wardrobeComment && (
                <div className="glass-panel p-6 rounded-xl border-l-4 border-azure-500 shadow-xl bg-azure/5">
                  <h4 className="text-[10px] font-black text-azure-400 uppercase tracking-[0.3em] mb-3 italic">Stylist’s Note.</h4>
                  <p className="text-sm text-gray-300 leading-relaxed italic font-merriweather">"{wardrobeComment}"</p>
                </div>
              )}

              {suggestions.map((item, idx) => (
                <div key={idx} className="glass-panel group p-1 transition-all duration-500 hover:scale-[1.01] rounded-2xl overflow-hidden shadow-2xl">
                  <div className="flex flex-col md:flex-row gap-8 p-8">
                    <div className="w-full md:w-64 flex flex-col gap-2 flex-shrink-0">
                      {/* Visual & Wardrobe Vault Doors Container */}
                      <div 
                        style={{ perspective: '1200px' }}
                        className="w-full aspect-[2/3] md:aspect-[3/4] md:h-96 relative overflow-hidden rounded-xl border border-white/10 bg-dk-800 group/visual select-none shadow-xl"
                      >
                        {/* Background View: Generated Image or Explorer or Wardrobe Pieces */}
                        {item.isAiGenerated || openingCardIdx === idx || isExplorer ? (
                          <div className="w-full h-full relative">
                            <img 
                              src={item.base64Image || item.previewUrl} 
                              alt={item.name} 
                              decoding="async"
                              loading="lazy"
                              onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.onerror = null;
                                  target.src = "/empty_filler.jpg";
                              }}
                              className={`w-full h-full object-cover object-top transition-transform duration-700 ${isExplorer ? 'blur-[2px] grayscale-[0.5] opacity-60' : 'group-hover/visual:scale-105'}`} 
                            />
                            {isExplorer && (
                              <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center space-y-3 bg-black/40 backdrop-blur-[1px]">
                                 <div className="w-10 h-10 border border-gold/40 rounded-full flex items-center justify-center bg-black/60 shadow-xl">
                                    <svg className="w-4 h-4 text-gold" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"></path></svg>
                                 </div>
                                 <div className="space-y-1">
                                    <p className="text-[8px] font-black text-gold uppercase tracking-[0.2em] leading-tight drop-shadow-md">AI Capability Preview</p>
                                    <p className="text-[7px] font-bold text-white/70 uppercase tracking-widest leading-tight">Low Fidelity Mode</p>
                                  </div>
                                 <button className="text-[8px] font-black text-white bg-gold/80 px-3 py-1.5 rounded-sm hover:bg-gold transition-all uppercase tracking-tighter shadow-lg transform active:scale-95">Upgrade for HD</button>
                              </div>
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-dk-900/80 via-transparent to-transparent opacity-60 pointer-events-none"></div>
                          </div>
                        ) : (
                          /* Behind closed doors: Wardrobe Pieces or Subtle Placeholder */
                          item.wardrobePhotos && item.wardrobePhotos.length > 1 ? (
                            <div className="w-full h-full flex flex-col gap-1.5 p-1.5 bg-dk-900/90">
                              <div className="flex-1 relative overflow-hidden rounded-lg border border-white/5">
                                <img 
                                  src={item.wardrobePhotos[0]} 
                                  alt="Piece 1"
                                  className="w-full h-full object-cover object-center filter brightness-90"
                                />
                                <div className="absolute top-1.5 left-1.5 bg-black/80 backdrop-blur-sm px-2 py-0.5 rounded text-[8px] font-black text-azure-400 uppercase tracking-widest border border-azure-500/30">
                                  Piece 1
                                </div>
                              </div>
                              <div className="flex-1 relative overflow-hidden rounded-lg border border-white/5">
                                <img 
                                  src={item.wardrobePhotos[1]} 
                                  alt="Piece 2"
                                  className="w-full h-full object-cover object-center filter brightness-90"
                                />
                                <div className="absolute top-1.5 left-1.5 bg-black/80 backdrop-blur-sm px-2 py-0.5 rounded text-[8px] font-black text-gold-400 uppercase tracking-widest border border-gold-500/30">
                                  Piece 2
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="w-full h-full relative">
                              <img 
                                src={item.previewUrl || "/empty_filler.jpg"} 
                                alt={item.name} 
                                className="w-full h-full object-cover object-top opacity-20 filter blur-[1px]"
                              />
                              <div className="absolute inset-0 bg-gradient-to-b from-[#0a0c12]/80 via-transparent to-[#0a0c12]/90"></div>
                            </div>
                          )
                        )}

                        {/* 3D Wardrobe Vault Double Doors (Deferred Look State) */}
                        {(!item.isAiGenerated || openingCardIdx === idx) && !isExplorer && (
                          <div className="absolute inset-0 z-20 pointer-events-auto">
                            {/* Left Door */}
                            <div 
                              style={{
                                transformOrigin: 'left center',
                                transform: openingCardIdx === idx ? 'rotateY(-115deg)' : 'rotateY(0deg)',
                                opacity: openingCardIdx === idx ? 0 : 1,
                                transition: 'transform 1000ms cubic-bezier(0.16, 1, 0.3, 1), opacity 800ms ease-out'
                              }}
                              className="absolute top-0 left-0 bottom-0 w-1/2 bg-gradient-to-r from-[#08090d] via-[#121622] to-[#0d1018] border-r border-azure-500/40 shadow-2xl overflow-hidden"
                            >
                              {/* Cyber ribbing texture and seam handle */}
                              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_left,_var(--tw-gradient-stops))] from-azure-900/15 via-transparent to-transparent"></div>
                              <div className="absolute left-3 top-3 bottom-3 w-[1px] bg-white/5"></div>
                              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex flex-col items-center gap-1">
                                <div className="w-1.5 h-16 rounded-full bg-gradient-to-b from-azure-400/30 via-azure-300 to-azure-400/30 border border-azure-400/50 shadow-[0_0_8px_rgba(0,127,255,0.4)]"></div>
                              </div>
                              <div className="absolute bottom-2.5 left-2.5 text-[7px] font-mono text-gray-400/80 uppercase tracking-widest">
                                LOOK 0{idx + 1}
                              </div>
                            </div>

                            {/* Right Door */}
                            <div 
                              style={{
                                transformOrigin: 'right center',
                                transform: openingCardIdx === idx ? 'rotateY(115deg)' : 'rotateY(0deg)',
                                opacity: openingCardIdx === idx ? 0 : 1,
                                transition: 'transform 1000ms cubic-bezier(0.16, 1, 0.3, 1), opacity 800ms ease-out'
                              }}
                              className="absolute top-0 right-0 bottom-0 w-1/2 bg-gradient-to-l from-[#08090d] via-[#121622] to-[#0d1018] border-l border-azure-500/40 shadow-2xl overflow-hidden"
                            >
                              {/* Cyber ribbing texture and seam handle */}
                              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_right,_var(--tw-gradient-stops))] from-azure-900/15 via-transparent to-transparent"></div>
                              <div className="absolute right-3 top-3 bottom-3 w-[1px] bg-white/5"></div>
                              <div className="absolute left-2 top-1/2 -translate-y-1/2 flex flex-col items-center gap-1">
                                <div className="w-1.5 h-16 rounded-full bg-gradient-to-b from-azure-400/30 via-azure-300 to-azure-400/30 border border-azure-400/50 shadow-[0_0_8px_rgba(0,127,255,0.4)]"></div>
                              </div>
                              <div className="absolute bottom-2.5 right-2.5 text-[7px] font-mono text-gray-400/80 uppercase tracking-widest">
                                VAULT
                              </div>
                            </div>

                            {/* Center Seam Light */}
                            <div 
                              className={`absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-[2px] pointer-events-none transition-all duration-500 ${
                                openingCardIdx === idx ? 'opacity-0' : 'opacity-100'
                              } ${
                                renderingCardIdx === idx 
                                  ? 'bg-azure-400 shadow-[0_0_15px_#007fff] animate-pulse' 
                                  : 'bg-azure-500/40 shadow-[0_0_6px_rgba(0,127,255,0.25)]'
                              }`}
                            ></div>

                            {/* Center Interactive Overlay: 'Unveil the look' or Synthesis Spinner */}
                            <div 
                              onClick={() => {
                                if (renderingCardIdx === null && openingCardIdx === null) {
                                  handleRenderSingleLook(item, idx);
                                }
                              }}
                              className={`absolute inset-0 z-30 flex flex-col items-center justify-center p-4 text-center transition-all duration-300 ${
                                openingCardIdx === idx ? 'opacity-0 scale-90 pointer-events-none' : 'opacity-100'
                              } ${renderingCardIdx !== null ? 'cursor-wait' : 'cursor-pointer group/door'}`}
                            >
                              {renderingCardIdx === idx ? (
                                <div className="flex flex-col items-center justify-center space-y-3 bg-black/85 backdrop-blur-md px-6 py-5 rounded-2xl border border-azure-500/50 shadow-[0_0_30px_rgba(0,127,255,0.4)] animate-fade-in max-w-[200px]">
                                  <div className="relative w-12 h-12 flex items-center justify-center">
                                    <div className="absolute inset-0 rounded-full border-2 border-azure-500/20 animate-ping"></div>
                                    <div className="w-10 h-10 rounded-full border-2 border-azure-500/40 border-t-azure-400 animate-spin"></div>
                                    <span className="text-xs text-azure-400 animate-pulse">✨</span>
                                  </div>
                                  <div className="space-y-0.5">
                                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-azure-300 animate-pulse">
                                      Synthesizing Look...
                                    </p>
                                    <p className="text-[7.5px] text-gray-400 font-mono uppercase tracking-wider">
                                      Editorial Visualizer
                                    </p>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex flex-col items-center justify-center space-y-3 bg-black/75 backdrop-blur-md px-6 py-5 rounded-2xl border border-azure-500/30 group-hover/door:border-azure-400 shadow-[0_0_20px_rgba(0,127,255,0.2)] group-hover/door:shadow-[0_0_35px_rgba(0,127,255,0.5)] transition-all duration-300 transform group-hover/door:scale-105 max-w-[210px]">
                                  <div className="w-11 h-11 rounded-full bg-azure-500/10 border border-azure-400/40 flex items-center justify-center group-hover/door:bg-azure-500/20 group-hover/door:border-azure-300 shadow-[0_0_15px_rgba(0,127,255,0.25)] transition-all duration-300">
                                    <svg className="w-5 h-5 text-azure-400 group-hover/door:scale-110 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.75" d="M8 3v18M16 3v18M4 3h16a1 1 0 011 1v16a1 1 0 01-1 1H4a1 1 0 01-1-1V4a1 1 0 011-1zM10 12h.01M14 12h.01" />
                                    </svg>
                                  </div>
                                  
                                  <div className="space-y-0.5">
                                    <p className="text-[10.5px] font-black uppercase tracking-[0.25em] text-white group-hover/door:text-azure-300 transition-colors drop-shadow-md">
                                      Unveil the look
                                    </p>
                                    <p className="text-[7.5px] font-bold text-azure-400/80 uppercase tracking-widest font-mono">
                                      {item.fromWardrobe || (item.wardrobePhotos && item.wardrobePhotos.length > 0) ? "Tap to open wardrobe" : "Tap to synthesize look"}
                                    </p>
                                  </div>

                                  {item.wardrobePhotos && item.wardrobePhotos.length > 1 && (
                                    <div className="mt-1 px-2.5 py-0.5 rounded-full bg-gold-500/10 border border-gold-500/30 text-[7px] font-bold text-gold-300 uppercase tracking-wider flex items-center gap-1">
                                      <span>✨</span>
                                      <span>2 Closet Pieces Paired</span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Download Controls */}
                      {isExplorer ? (
                        /* Explorer Tier Gated Download Button (Option A) */
                        <button
                          onClick={handleExplorerDownloadClick}
                          className="w-full py-2 px-3 bg-white/5 hover:bg-gold-500/10 hover:border-gold-500/40 border border-white/10 rounded-xl text-[9px] font-black uppercase tracking-widest text-gray-400 hover:text-gold-400 transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-95 shadow-md group/btn"
                          title="Downloading on-model style looks is reserved for Standard and Premium tiers"
                        >
                          <svg className="w-3.5 h-3.5 text-gold-400/80 group-hover/btn:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          <span>🔒 Upgrade to Download</span>
                        </button>
                      ) : item.isAiGenerated ? (
                        /* Unveiled Look Download (End State) */
                        <button
                          onClick={() => handleDownloadImage(item.base64Image || item.previewUrl, `synclothe-${item.name}`)}
                          className="w-full py-2 px-3 bg-white/5 hover:bg-azure/20 hover:border-azure/40 border border-white/10 rounded-xl text-[9px] font-black uppercase tracking-widest text-azure-400 hover:text-white transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-95 shadow-md group/btn"
                          title="Save on-model style look to your device"
                        >
                          <svg className="w-3.5 h-3.5 text-azure-400 group-hover/btn:translate-y-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                          <span>Download Style Look</span>
                        </button>
                      ) : (
                        /* Pre-unveil Actions (Vault Door Closed) */
                        <div className="flex flex-col gap-1.5">
                          <button
                            onClick={() => {
                              if (renderingCardIdx === null && openingCardIdx === null) {
                                handleRenderSingleLook(item, idx);
                              }
                            }}
                            disabled={renderingCardIdx !== null || openingCardIdx !== null}
                            className="w-full py-2 px-3 bg-azure/10 hover:bg-azure/25 hover:border-azure/40 border border-azure-500/30 rounded-xl text-[9px] font-black uppercase tracking-widest text-azure-300 hover:text-white transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-95 shadow-md group/btn disabled:opacity-50 disabled:cursor-wait"
                            title="Synthesize on-model look"
                          >
                            <span className="text-azure-400">✨</span>
                            <span>{renderingCardIdx === idx ? 'Synthesizing Look...' : 'Unveil on Model'}</span>
                          </button>

                          {item.wardrobePhotos && item.wardrobePhotos.length > 1 ? (
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleDownloadImage(item.wardrobePhotos![0], `synclothe-${item.name}-piece1`)}
                                className="flex-1 py-1.5 px-2 bg-white/5 hover:bg-azure/20 hover:border-azure/40 border border-white/10 rounded-lg text-[8px] font-bold uppercase tracking-wider text-azure-400 hover:text-white transition-all flex items-center justify-center gap-1 cursor-pointer active:scale-95 shadow-sm group/btn"
                                title="Download Piece 1"
                              >
                                <span>Piece 1</span>
                              </button>
                              <button
                                onClick={() => handleDownloadImage(item.wardrobePhotos![1], `synclothe-${item.name}-piece2`)}
                                className="flex-1 py-1.5 px-2 bg-white/5 hover:bg-gold/20 hover:border-gold/40 border border-white/10 rounded-lg text-[8px] font-bold uppercase tracking-wider text-gold-400 hover:text-white transition-all flex items-center justify-center gap-1 cursor-pointer active:scale-95 shadow-sm group/btn"
                                title="Download Piece 2"
                              >
                                <span>Piece 2</span>
                              </button>
                            </div>
                          ) : item.wardrobePhotos && item.wardrobePhotos.length === 1 ? (
                            <button
                              onClick={() => handleDownloadImage(item.wardrobePhotos![0], `synclothe-${item.name}-piece`)}
                              className="w-full py-1.5 px-2 bg-white/5 hover:bg-azure/20 hover:border-azure/40 border border-white/10 rounded-lg text-[8px] font-bold uppercase tracking-wider text-azure-400 hover:text-white transition-all flex items-center justify-center gap-1 cursor-pointer active:scale-95 shadow-sm group/btn"
                              title="Download Closet Piece"
                            >
                              <span>Closet Piece</span>
                            </button>
                          ) : null}
                        </div>
                      )}
                    </div>

                    <div className="flex-grow flex flex-col justify-between">
                      <div>
                        <div className="flex justify-between items-start mb-4">
                           <div>
                              <span className="attribute-badge mb-2 inline-block bg-dk-800 text-azure-400 border-azure/20">
                                {item.suggestionCategory?.replace(/_/g, ' ') || (item.fromWardrobe ? 'Inventory' : 'Discovery')}
                              </span>
                              <h3 className="text-xl font-black text-white italic tracking-tighter uppercase leading-none">{item.name}</h3>
                           </div>
                        </div>
                        <p className="text-gray-400 text-sm leading-relaxed mb-6 max-w-2xl font-inter">{item.description}</p>
                        
                        {(item.hairstyle || (item.accessories && item.accessories.length > 0)) && (
                          <div className="flex flex-wrap gap-2 mb-6 border-t border-white/5 pt-4">
                            {item.hairstyle && (
                              <div className="px-3 py-1.5 bg-azure-900/40 border border-azure-500/20 rounded-lg text-[10px] text-azure-300 font-bold uppercase tracking-wider flex items-center gap-1.5">
                                <span>✂️</span>
                                <span>Hair: {item.hairstyle}</span>
                              </div>
                            )}
                            {item.accessories && item.accessories.length > 0 && (
                              <div className="px-3 py-1.5 bg-gold-900/30 border border-gold-500/20 rounded-lg text-[10px] text-gold-400 font-bold uppercase tracking-wider flex items-center gap-1.5">
                                <span>💎</span>
                                <span>Acc: {item.accessories.join(' • ')}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 border-t border-white/5 pt-6">
                        <AttributeBlock label="Contextual" text={item.attributes.contextual} />
                        <AttributeBlock label="Cultural" text={item.attributes.cultural} />
                        <AttributeBlock label="Physical" text={item.attributes.physical} />
                        <AttributeBlock label="Aesthetic" text={item.attributes.aesthetic} />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-full min-h-[600px] flex items-center justify-center glass-panel border-dashed border-white/10 rounded-2xl">
               {isProcessing ? (
                   <div className="text-center space-y-8">
                      <div className="relative w-24 h-24 mx-auto">
                        <div className="absolute inset-0 border-4 border-azure/20 rounded-full"></div>
                        <div className="absolute inset-0 border-4 border-transparent border-t-azure rounded-full animate-spin"></div>
                      </div>
                      <p className="text-[10px] text-azure-400 uppercase font-black tracking-[0.4em] animate-pulse">Computing Logic Tier: {subscriptionTier}...</p>
                   </div>
               ) : (
                  <div className="text-center opacity-40">
                    <p className="text-[10px] font-bold uppercase tracking-[0.5em] text-gray-300 text-center">Awaiting Strategic Inputs...</p>
                  </div>
               )}
            </div>
          )}
        </div>
      </div>

      {/* Explorer Demographics Modal Portal */}
      {showDemographicsModal && !isProcessing && createPortal(
        <div 
          className="fixed inset-0 bg-black/85 backdrop-blur-md flex items-center justify-center z-[99999] p-4 animate-fade-in"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowDemographicsModal(false);
          }}
        >
          <div 
            role="dialog"
            aria-modal="true"
            aria-labelledby="demographics-modal-title"
            className="bg-[#0b0f19] border-2 border-azure-500/60 p-6 md:p-8 rounded-3xl max-w-md w-full shadow-[0_0_80px_rgba(0,0,0,0.95)] text-white relative overflow-hidden animate-scale-up"
          >
            {/* Ambient Lighting Accents */}
            <div className="absolute top-0 right-0 w-48 h-48 bg-azure/10 rounded-full blur-3xl pointer-events-none -mr-16 -mt-16"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-gold/5 rounded-full blur-3xl pointer-events-none -ml-16 -mb-16"></div>

            {/* Header */}
            <div className="flex justify-between items-start border-b border-white/10 pb-4 mb-6 relative z-10">
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-[9px] font-black uppercase text-azure-400 tracking-[0.25em] bg-azure-500/10 px-2.5 py-0.5 rounded border border-azure-500/20">
                    Explorer Intelligence Profile
                  </span>
                </div>
                <h2 id="demographics-modal-title" className="text-xl font-black italic uppercase tracking-tighter text-white">
                  Demographic <span className="text-azure-500">Parameters</span>
                </h2>
                <p className="text-[11px] text-gray-400 mt-1 leading-relaxed">
                  Calibrate silhouette cuts, garment styling, and AI visual model representation.
                </p>
              </div>
              <button
                onClick={() => setShowDemographicsModal(false)}
                className="text-gray-400 hover:text-white text-sm font-bold p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                aria-label="Close modal"
              >
                ✕
              </button>
            </div>

            <div className="space-y-6 relative z-10">
              {/* Gender Selection */}
              <div>
                <div className="flex items-center justify-between mb-2.5">
                  <label className="text-[10px] uppercase font-black text-gray-300 tracking-wider">
                    Gender Identity
                  </label>
                  <span className="text-[9px] text-azure-400 font-mono font-bold">
                    Target Apparel Alignment
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2.5">
                  {(['Female', 'Male', 'Non-binary', 'Other'] as const).map((genderOption) => {
                    const isSelected = selectedGender === genderOption;
                    return (
                      <button
                        key={genderOption}
                        type="button"
                        onClick={() => setSelectedGender(genderOption)}
                        className={`p-3 rounded-xl border text-xs font-bold transition-all flex items-center justify-between ${
                          isSelected
                            ? 'border-azure-500 bg-azure-500/20 text-white shadow-[0_0_15px_rgba(0,180,216,0.3)] ring-1 ring-azure-500/50'
                            : 'border-white/10 bg-white/5 text-gray-400 hover:border-white/20 hover:text-gray-200'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <span className="text-sm">
                            {genderOption === 'Female' && '♀'}
                            {genderOption === 'Male' && '♂'}
                            {genderOption === 'Non-binary' && '⚥'}
                            {genderOption === 'Other' && '✦'}
                          </span>
                          <span>{genderOption}</span>
                        </span>
                        {isSelected && (
                          <span className="w-2 h-2 rounded-full bg-azure-400 shadow-[0_0_6px_rgba(0,180,216,0.8)]"></span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Age Selection */}
              <div>
                <div className="flex items-center justify-between mb-2.5">
                  <label className="text-[10px] uppercase font-black text-gray-300 tracking-wider">
                    Age (Years)
                  </label>
                  <span className="text-[9px] text-gray-400 font-mono">
                    Range: 16 – 100
                  </span>
                </div>
                
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setSelectedAge(prev => Math.max(16, (Number(prev) || 25) - 1))}
                    className="w-11 h-11 rounded-xl bg-white/5 border border-white/10 hover:border-azure/40 text-gray-300 hover:text-white font-black text-lg flex items-center justify-center transition-all hover:bg-azure-500/10 active:scale-95"
                  >
                    −
                  </button>
                  <div className="flex-1 relative">
                    <input
                      type="number"
                      min={16}
                      max={100}
                      value={selectedAge}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        setSelectedAge(isNaN(val) ? ('' as any) : val);
                      }}
                      className="w-full text-center input-glass text-lg font-mono font-black text-white bg-[#0e1424] border border-white/15 rounded-xl py-2.5 focus:border-azure focus:ring-1 focus:ring-azure focus:outline-none"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-mono text-gray-500 uppercase">
                      YRS
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedAge(prev => Math.min(100, (Number(prev) || 25) + 1))}
                    className="w-11 h-11 rounded-xl bg-white/5 border border-white/10 hover:border-azure/40 text-gray-300 hover:text-white font-black text-lg flex items-center justify-center transition-all hover:bg-azure-500/10 active:scale-95"
                  >
                    +
                  </button>
                </div>

                {/* Quick preset chips */}
                <div className="flex items-center gap-1.5 mt-2.5">
                  <span className="text-[9px] text-gray-500 uppercase font-mono mr-1">Presets:</span>
                  {[20, 25, 30, 40, 50, 60].map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => setSelectedAge(preset)}
                      className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold transition-all ${
                        selectedAge === preset
                          ? 'bg-azure-500 text-black shadow-[0_0_8px_rgba(0,180,216,0.6)]'
                          : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/5'
                      }`}
                    >
                      {preset}
                    </button>
                  ))}
                </div>
              </div>

              {/* Informative Guidance */}
              <div className="p-3 bg-azure/5 border border-azure-500/20 rounded-xl">
                <p className="text-[10px] text-gray-300 leading-relaxed">
                  <strong className="text-azure-400 uppercase font-bold tracking-wider">AI Impact: </strong>
                  Tailors cuts, silhouettes, garments ({selectedGender === 'Female' ? 'dresses, jewelry, feminine tailoring' : selectedGender === 'Male' ? 'suits, menswear cuts, accessories' : 'androgynous/fluid contemporary attire'}), and photorealistic visual model styling.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="pt-2 flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => setShowDemographicsModal(false)}
                  className="w-full sm:w-1/3 py-3 px-4 rounded-xl border border-white/10 hover:border-white/20 text-gray-300 hover:text-white text-xs font-bold uppercase tracking-wider transition-all bg-white/5 hover:bg-white/10 text-center"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDemographics}
                  disabled={isProcessing}
                  className="w-full sm:w-2/3 btn-azure py-3 px-4 uppercase tracking-[0.2em] text-xs font-black text-center shadow-[0_0_25px_rgba(0,180,216,0.35)] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessing ? 'Synthesizing...' : 'Synthesize Look ✦'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

const AttributeBlock = React.memo(({ label, text }: { label: string, text: string }) => (
  <div>
    <h4 className="text-[9px] font-black text-azure-400 uppercase tracking-widest mb-2 flex items-center">
       <span className="w-1 h-1 bg-azure-500 rounded-full mr-2"></span>
       {label}
    </h4>
    <p className="text-[11px] text-gray-400 leading-tight">{text}</p>
  </div>
));

export default StylingEngine;
